import RestrictionWrapper from "features/auth/restrictionWrapper"
import { AccessRole, AuthenticationStatus, DeviceSize, FormStatus } from "types"
import { ReactComponent as HomeIcon } from 'assets/icons/house.svg'
import { ReactComponent as StoragesIcon } from 'assets/icons/home_storage.svg'
import { ReactComponent as LoginIcon } from 'assets/icons/login.svg'
import { ReactComponent as SignUpIcon } from 'assets/icons/group_add.svg'
import { useTranslation } from "react-i18next"
import { ReactComponent as CategoryIcon } from 'assets/icons/category.svg'
import { ReactComponent as LocationIcon } from 'assets/icons/location.svg'
import { ReactComponent as DescriptionIcon } from 'assets/icons/description.svg'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg'
import { ReactComponent as CustomersIcon } from 'assets/icons/group.svg'
import { ReactComponent as SuppliersIcon } from 'assets/icons/shipping.svg'
import { ReactComponent as BoxIcon } from 'assets/icons/box.svg'
import { ReactComponent as HistoryIcon } from 'assets/icons/restore.svg'
import { ReactComponent as NotificationIcon } from 'assets/icons/notifications.svg'
import React, { JSX, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import SidebarGroup from "../sidebarGroup"
import SidebarDropdown from "../SidebarDropdown"
import useStorages from "hooks/useStorages"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import { APP_CONFIG } from "constants/constants";
import { motion, AnimatePresence } from "motion/react"
import useWindowDimensions from "hooks/useWindowDimensions"
import useLayout from "hooks/useLayout"
import SidebarRow from "../SidebarRow"
import useAccess from "@/hooks/useAccess"
import ProfileButton from "components/user/profileButton"
import OrdnatLogo from "components/shared/OrdnatLogo"
import useNotifications from "@/hooks/useNotifications"

interface LeftRightSidebarProps {
    className?: string;
    storageId?: string;
    categoryId?: string;
    locationId?: string;
    customerId?: string;
    productId?: string;
}

const LeftSidebar: React.FC<LeftRightSidebarProps> = ({ 
    className, 
    storageId, 
    categoryId, 
    locationId,
    customerId, 
    productId 
}) => {
    const { deviceSize } = useWindowDimensions()
    const navigate = useNavigate()
    const { targetStorageDocId, targetStorageName, setTargetStorage } = useTargetStorage()

    const {
        showDialog,
        hideSidebar,
        sidebarIsVisible
    } = useLayout()

    const { storages, storageStatus, storage, isTransactionsEnabled, isCustomersAndSuppliersEnabled, isSharedAccessEnabled } = useStorages({ storageId })
    const showStorageNavigation = Boolean(storageId && storage)

    useEffect(() => {
        const loadedId = storage?.id || storageId
        const loadedName = storage?.name
        if (!loadedId || !loadedName) return
        if (loadedId !== targetStorageDocId) return
        if (loadedName === targetStorageName) return
        setTargetStorage(loadedId, loadedName)
    }, [storage?.id, storage?.name, storageId, targetStorageDocId, targetStorageName, setTargetStorage])

    const {
        allCategories,
        findCategory
    } = useCategories({ storageId })

    const {
        allLocations,
        findLocation
    } = useLocations({ storageId })

    const {
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    // Translation.
    const { t } = useTranslation();
    const { unreadCount } = useNotifications()
    const sidebarRef = useRef<HTMLDivElement>(null);

    const handleSelectStorage = (id: string, name: string) => {
        setTargetStorage(id, name)
        navigate('/storage')
    }

    const handleHelpAndSettingsClick = () => {
        showDialog('help_and_settings')
        hideSidebar()
    }

    // Define Tailwind classes as constants
    const sidebarClasses = `fixed top-14 lg:top-0 bottom-0 overflow-y-auto app-background ${className ? className : ''} z-[105] lg:z-0 w-3/4 sm:w-1/2 md:w-1/3`;

    const printCategoryHierarchy = (categoryId: string, level: number, key: number): JSX.Element | null => {
        let categoryData = findCategory(categoryId)
        if (!categoryData) return null
        if (categoryData.isDeleted) {
            return (
                <React.Fragment key={key}>
                    {allCategories
                        .filter(cat => cat && cat.relations?.parentCategory === categoryId)
                        .map((childCategory, key) => printCategoryHierarchy(childCategory.id as string, level, key))}
                </React.Fragment>
            )
        }
        return (
            <div key={`category-sidebarrow-${categoryId}`}>
                <SidebarRow
                    to={`/storage/categories/${categoryId}`}
                    text={`${Array(level).fill('—').join('')}${level ? ' ' : ''}${categoryData.name}`}
                />
                {allCategories
                    .filter(cat => cat && cat.relations?.parentCategory === categoryId)
                    .map((childCategory, key) => printCategoryHierarchy(childCategory.id as string, level + 1, key))}
            </div>
        )
    }

    const printLocationHierarchy = (itemId: string, level: number, key: number): JSX.Element | null => {
        let locationData = findLocation(itemId)
        if (!locationData) return null
        if (locationData.isDeleted) {
            return (
                <React.Fragment key={key}>
                    {allLocations
                        .filter(loc => loc && loc.relations?.parentLocation === itemId)
                        .map((childLocation, childKey) => printLocationHierarchy(childLocation.id as string, level, childKey))}
                </React.Fragment>
            )
        }
        return (
            <div key={`location-sidebarrow-${itemId}`}>
                <SidebarRow
                    to={`/storage/locations/${itemId}`}
                    text={`${Array(level).fill('—').join('')}${level ? ' ' : ''}${locationData.name}`}
                />
                {allLocations
                    .filter(loc => loc && loc.relations?.parentLocation === itemId)
                    .map((childLocation, childKey) => printLocationHierarchy(childLocation.id as string, level + 1, childKey))}
            </div>
        )
    }

    const sidebarElement =
        <div ref={sidebarRef} className={`relative min-h-full pt-4 lg:pt-0 pb-28`}>
            {/* Logo sits in the top bar on small screens, so the drawer starts with navigation */}
            <div className="hidden lg:flex h-16 items-center pl-4">
                <OrdnatLogo className="h-6 lg:h-8 pl-4" />
            </div>
            {/* Navigation items */}
            <RestrictionWrapper restrictedTo={AuthenticationStatus.online}>
                <SidebarDropdown title={storage?.name || targetStorageName || (storageStatus === FormStatus.LOADING ? t('loading') : t('storages'))} icon={<StoragesIcon />} closeOnClick={true}>
                    {/*<SidebarRow to="/storages/create" text={t('buttonAddNewStorage')} inset />*/}
                    <SidebarRow to={`/storages`} text={t('viewAll')} inset activeCheckExact={true} />
                    {storages && storages.map(listedStorage => {
                        if (listedStorage.isDeleted) return null
                        const listedStorageId = listedStorage.id as string
                        return (
                            <SidebarRow
                                key={listedStorageId}
                                text={listedStorage.name}
                                inset
                                active={listedStorageId === storageId}
                                onClick={() => handleSelectStorage(listedStorageId, listedStorage.name)}
                            />
                        )
                    })}
                </SidebarDropdown>
                {showStorageNavigation && 
                    <>
                        <SidebarRow to={`/storage`} icon={<HomeIcon />} text={t('buttonDashboard')} activeCheckExact={true} />
                        <SidebarRow to={`/storage/products`} icon={<BoxIcon />} text={t('products')} />
                        {isCustomersAndSuppliersEnabled() && <SidebarRow to={`/storage/customers`} icon={<CustomersIcon />} text={t('titleCustomers')} />}
                        {isCustomersAndSuppliersEnabled() && <SidebarRow to={`/storage/suppliers`} icon={<SuppliersIcon />} text={t('titleSuppliers')} />}
                        {isTransactionsEnabled() && <SidebarRow to={`/storage/transactions`} icon={<DescriptionIcon />} text={t('titleTransactions')} />}

                        <SidebarGroup title={t('categories')} icon={<CategoryIcon />}>
                            <SidebarRow to={`/storage/categories`} text={t('viewAll')} activeCheckExact={true} />
                            {allCategories
                                .filter(cat => cat && (!cat.relations?.parentCategory || !allCategories.some(c => c.id === cat.relations?.parentCategory)))
                                .map((category, key) => printCategoryHierarchy(category.id as string, 0, key))}
                        </SidebarGroup>
                        <SidebarGroup title={t('locations')} icon={<LocationIcon />}>
                            <SidebarRow to={`/storage/locations`} text={t('viewAll')} activeCheckExact={true} />
                            {allLocations
                                .filter(loc => loc && (!loc.relations?.parentLocation || !allLocations.some(c => c.id === loc.relations?.parentLocation)))
                                .map((item, key) => printLocationHierarchy(item.id as string, 0, key))}
                        </SidebarGroup>
                        { limitToRoles([AccessRole.OWNER, AccessRole.EDITOR]) &&
                            <SidebarGroup title={t('buttonSettings')} icon={<SettingsIcon />}>
                                <SidebarRow to={`/storage/statistics`} text={t('titleStatistics')} />
                                <SidebarRow to={`/storage/history`} text={t('titleHistory')} />
                                { limitToRoles([AccessRole.OWNER]) && <SidebarRow to={`/storage/edit`} text={t('editStorage')} /> }
                                {limitToRoles([AccessRole.OWNER]) && isSharedAccessEnabled() && <SidebarRow to={`/storage/access`} text={t('titleManageAccess')} />}
                                { limitToRoles([AccessRole.OWNER]) && <SidebarRow to={`/storage/delete`} text={t('buttonDeleteStorage')} /> }
                            </SidebarGroup>
                        }
                    </>
                }
            </RestrictionWrapper>
            <RestrictionWrapper restrictedTo={AuthenticationStatus.offline}>
                <SidebarRow to="/" icon={<HomeIcon />} text={t('navHome')} activeCheckExact={true} />
                <SidebarRow to="/sign-up" icon={<SignUpIcon />} text={t('navSignUp')} />
                <SidebarRow to="/login" icon={<LoginIcon />} text={t('navLogin')} />
            </RestrictionWrapper>
            <div className={`absolute bottom-0 w-full pb-2`}>
                <ProfileButton showName={true} className="border border-gray-400 mx-4 mb-2 w-[calc(100%-2rem)] box-border" />
                <RestrictionWrapper restrictedTo={AuthenticationStatus.online}>
                    <SidebarRow
                        to="/notifications"
                        icon={<NotificationIcon />}
                        text={t('titleNotifications')}
                        style="border"
                        badge={unreadCount > 0 ? (
                            <span className="min-w-5 h-5 px-1 bg-red-500 rounded-md flex items-center justify-center text-xs text-white">{unreadCount}</span>
                        ) : undefined}
                    />
                </RestrictionWrapper>
                <SidebarRow icon={<SettingsIcon />} text={t('titleHelpAndSettings')} onClick={handleHelpAndSettingsClick} style={`border`} />
            </div>
        </div>

    // Return the sidebar component.
    if (deviceSize === DeviceSize.LG) {
        return <div className={sidebarClasses}>{sidebarElement}</div>
    } else {
        return (
            <AnimatePresence>
                {sidebarIsVisible &&
                    <motion.div
                        initial={deviceSize === DeviceSize.SM ? {
                            left: "-80%"
                        } : {
                            left: "-60%"
                        }}
                        animate={{
                            left: "0%",
                            transition: {
                                duration: APP_CONFIG.SHORT_ENTER_ANIMATION_DURATION
                            }
                        }}
                        exit={deviceSize === DeviceSize.SM ? {
                            left: "-80%",
                            transition: {
                                duration: APP_CONFIG.SHORT_EXIT_ANIMATION_DURATION
                            }
                        } : {
                            left: "-60%",
                            transition: {
                                duration: APP_CONFIG.SHORT_EXIT_ANIMATION_DURATION
                            }
                        }}
                        className={sidebarClasses}
                    >
                    {sidebarElement}
                </motion.div>}
            </AnimatePresence>
        )
    }
}

export default LeftSidebar

